import { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tab from "@mui/material/Tab";
import TablePagination from "@mui/material/TablePagination";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import LinearProgress from "@mui/material/LinearProgress";
import IconButton from "@mui/material/IconButton";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import { useAuthz } from "context/AuthzContext";
import { listClientJobs, updateClientJob, listMasterServices, listTenantUsers, listTasks, updateTask, listClientProfiles } from "services/tenantUserAdminService";
import { TC, FS, FW } from "../tokens/text";
import { getServiceLabel, TASK_TYPE_LABELS } from "../constants/serviceLabels";

// â”€â”€â”€ Constants â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const CARD_BG     = "#ffffff";
const CARD_BORDER = "1px solid #d8e6f7";

const ROWS_PER_PAGE = 20;

const DURATION_OPTIONS = [
  { value: "3months", label: "3 Months" },
  { value: "6months", label: "6 Months" },
  { value: "all",     label: "All" },
];

function applyDurationFilter(jobs, duration) {
  if (duration === "all") return jobs;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const cutoff = new Date(today);
  cutoff.setMonth(cutoff.getMonth() + (duration === "3months" ? 3 : 6));
  return jobs.filter(j => !j.dueDate || new Date(j.dueDate) <= cutoff);
}

const SELECT_SX = {
  color: TC.primary, fontSize: 13,
  background: "#f4f8ff",
  border: "1px solid #d8e6f7",
  borderRadius: 2,
  "& .MuiSvgIcon-root": { color: TC.primary },
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
};

const STATUS_CFG = {
  not_started: { color: "#78909c", label: "Not Started", glow: "none" },
  pending:     { color: "#ff9800", label: "Pending",     glow: "0 0 6px 1px rgba(255,152,0,0.6)" },
  in_progress: { color: "#2196f3", label: "In Progress", glow: "0 0 6px 1px rgba(33,150,243,0.7)" },
  review:      { color: "#9c27b0", label: "Review",      glow: "0 0 6px 1px rgba(156,39,176,0.6)" },
  approved:    { color: "#00bcd4", label: "Approved",    glow: "0 0 6px 1px rgba(0,188,212,0.6)" },
  filed:       { color: "#4caf50", label: "Filed",       glow: "0 0 6px 1px rgba(76,175,80,0.6)" },
  completed:   { color: "#4caf50", label: "Completed",   glow: "0 0 6px 1px rgba(76,175,80,0.6)" },
  invoiced:    { color: "#4caf50", label: "Invoiced",    glow: "0 0 6px 1px rgba(76,175,80,0.6)" },
  cancelled:   { color: "#9e9e9e", label: "Cancelled",   glow: "none" },
  overdue:     { color: "#f44336", label: "Overdue",     glow: "0 0 6px 1px rgba(244,67,54,0.7)" },
};

// Returns background + left-border style based on how close the due date is
function dueDateRowStyle(dueDate) {
  if (!dueDate) return {};
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days  = Math.ceil((new Date(dueDate) - today) / 86400000);
  if (days < 0)   return { background: "rgba(211,47,47,0.06)",  borderLeft: "3px solid rgba(211,47,47,0.55)"  };
  if (days <= 14) return { background: "rgba(255,109,0,0.06)",  borderLeft: "3px solid rgba(255,109,0,0.5)"   };
  if (days <= 30) return { background: "rgba(255,152,0,0.05)",  borderLeft: "3px solid rgba(255,152,0,0.45)"  };
  return              { background: "rgba(56,142,60,0.04)",  borderLeft: "3px solid rgba(56,142,60,0.35)"  };
}

const col = {
  label:    { flex: "0 0 200px", minWidth: 160 },
  client:   { flex: "1 1 180px", minWidth: 130 },
  period:   { flex: "0 0 110px", minWidth: 90 },
  due:      { flex: "0 0 110px", minWidth: 80 },
  manager:  { flex: "0 0 150px", minWidth: 110 },
  progress: { flex: "0 0 130px", minWidth: 100 },
};

// Maps serviceId â†’ jobTab index inside CompanyJobsTab (mirrors JOB_SERVICE_TABS in clientprofiledetail)
const SERVICE_TAB_INDEX = {
  payroll:             0,
  vat:                 1,
  accounts:            2,
  corporation_tax:     3,
  company_secretarial: 4,
  ad01_address_change: 5,
};

// Maps auto-task types â†’ the same profile tab index as the equivalent service
const TASK_TYPE_TAB_INDEX = {
  YEAR_END:               2, // accounts
  CONFIRMATION_STATEMENT: 4, // company_secretarial
  ACCOUNTS_PREPARATION:   2, // accounts
  CT600:                  3, // corporation_tax
  VAT_RETURN:             1, // vat
  PAYROLL:                0, // payroll
};

function overdueDays(dueDateStr) {
  if (!dueDateStr) return null;
  const diff = Math.floor((new Date(dueDateStr) - new Date()) / 86400000);
  return diff;
}

// Derives the effective progress status from task completion stats + due date
function derivedStatus(job) {
  const { status, completedCount = 0, taskCount = 0, dueDate } = job;
  if (status === "cancelled") return "cancelled";
  if (status === "invoiced")  return "invoiced";
  if (status === "filed")     return "filed";
  if (taskCount > 0 && completedCount >= taskCount) return "filed";
  if (status === "approved")  return "approved";
  if (status === "review")    return "review";
  const days = overdueDays(dueDate);
  if (days !== null && days < 0) return "overdue";
  if (completedCount > 0)    return "in_progress";
  return "not_started";
}

function DueChip({ dueDate }) {
  const days = overdueDays(dueDate);
  if (days === null) return <VuiTypography sx={{ color: TC.primary, fontSize: 13 }}>-</VuiTypography>;
  if (days < 0)  return <Chip label={`${Math.abs(days)}d overdue`} size="small" sx={{ bgcolor: "rgba(244,67,54,0.18)", color: "#f44336", fontSize: "0.65rem", height: 18, fontWeight: 700 }} />;
  if (days <= 7) return <Chip label={`${days}d left`} size="small" sx={{ bgcolor: "rgba(255,152,0,0.18)", color: "#ff9800", fontSize: "0.65rem", height: 18, fontWeight: 700 }} />;
  return <VuiTypography sx={{ color: TC.primary, fontSize: 13 }}>{dueDate}</VuiTypography>;
}

function ProgressBar({ completed, total }) {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <VuiBox display="flex" alignItems="center" gap={0.8}>
      <VuiBox sx={{ flex: 1 }}>
        <LinearProgress variant="determinate" value={pct} sx={{
          height: 5, borderRadius: 3,
          bgcolor: "#7a94b0",
          "& .MuiLinearProgress-bar": { bgcolor: pct === 100 ? "#4caf50" : "#2196f3", borderRadius: 3 },
        }} />
      </VuiBox>
      <VuiTypography sx={{ color: TC.primary, fontSize: 13, whiteSpace: "nowrap" }}>
        {completed}/{total}
      </VuiTypography>
    </VuiBox>
  );
}

function StatusSelect({ job, tenantId, onChange }) {
  const [saving, setSaving] = useState(false);

  async function handleChange(e) {
    setSaving(true);
    try {
      if (job._isTask) {
        await updateTask(tenantId, job.id, { status: e.target.value });
      } else {
        await updateClientJob(tenantId, job.id, { status: e.target.value });
      }
      onChange(job.id, e.target.value);
    } finally {
      setSaving(false);
    }
  }

  const cfg = STATUS_CFG[derivedStatus(job)] || {};
  const taskStatuses = ["pending", "in_progress", "completed", "cancelled"];
  const jobStatuses  = Object.keys(STATUS_CFG).filter(k => k !== "completed" && k !== "overdue");

  return saving
    ? <CircularProgress size={16} sx={{ color: cfg.color }} />
    : (
      <Select
        value={job.status || "pending"}
        onChange={handleChange}
        size="small"
        sx={{
          color: cfg.color, fontSize: "0.72rem", fontWeight: 700,
          background: `${cfg.color}18`,
          border: `1px solid ${cfg.color}55`,
          borderRadius: "6px",
          height: 26,
          ".MuiSelect-icon": { color: cfg.color, fontSize: "1rem" },
          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
        }}
      >
        {(job._isTask ? taskStatuses : jobStatuses).map(k => (
          <MenuItem key={k} value={k} sx={{ fontSize: "0.75rem" }}>{STATUS_CFG[k]?.label || k}</MenuItem>
        ))}
      </Select>
    );
}

// â”€â”€â”€ Assign select â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function AssignSelect({ job, tenantId, users, onChange }) {
  const [saving, setSaving] = useState(false);

  async function handleChange(e) {
    const uid = e.target.value;
    setSaving(true);
    try {
      const user = users.find((u) => (u.uid || u.id) === uid);
      const assignedName = uid === "" ? null : (user?.displayName || user?.email || uid);
      const payload = { assignedTo: uid === "" ? null : uid, assignedName };
      if (job._isTask) {
        await updateTask(tenantId, job.id, payload);
      } else {
        await updateClientJob(tenantId, job.id, payload);
      }
      onChange(job.id, uid === "" ? null : uid, assignedName);
    } finally {
      setSaving(false);
    }
  }

  const currentUid = job.assignedTo || "";

  if (saving) return <CircularProgress size={16} sx={{ color: TC.secondary }} />;

  return (
    <Select
      value={currentUid}
      onChange={handleChange}
      size="small"
      displayEmpty
      onClick={(e) => e.stopPropagation()}
      sx={{
        color: currentUid ? "#0057d8" : TC.tertiary,
        fontSize: "0.72rem",
        background: currentUid ? "rgba(0,87,216,0.08)" : "#f4f8ff",
        border: currentUid ? "1px solid rgba(0,87,216,0.4)" : "1px solid #d8e6f7",
        borderRadius: "6px",
        height: 26,
        minWidth: 110,
        maxWidth: 140,
        fontWeight: currentUid ? 600 : 400,
        ".MuiSelect-icon": { color: currentUid ? "#0057d8" : TC.secondary, fontSize: "1rem" },
        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
        "& .MuiSelect-select": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
      }}
    >
      <MenuItem value="" sx={{ fontSize: "0.75rem", color: TC.secondary, fontStyle: "italic" }}>
        Unassigned
      </MenuItem>
      {users.map((u) => {
        const uid   = u.uid || u.id;
        const label = u.displayName || u.email || uid;
        return (
          <MenuItem key={uid} value={uid} sx={{ fontSize: "0.75rem" }}>
            {label}
          </MenuItem>
        );
      })}
    </Select>
  );
}

// â”€â”€â”€ Header row â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function TableHeader() {
  const cell = (label, style) => (
    <VuiTypography
      sx={{ color: TC.secondary, fontSize: 11, fontWeight: 700,
            textTransform: "uppercase", letterSpacing: "0.05em", ...style }}>
      {label}
    </VuiTypography>
  );
  return (
    <VuiBox px={2} py={1} sx={{ borderBottom: "1px solid #e4eef8" }}>
      {/* Top row on xs: left area intentionally blank, due on right */}
      <VuiBox display="flex" alignItems="center" justifyContent="space-between" gap={1.5}>
        <VuiBox sx={{ flex: 1 }} />
        <VuiBox sx={{ display: { xs: "block", sm: "none" } }}>{cell("Due", col.due)}</VuiBox>
      </VuiBox>

      {/* Full header row for sm+: omit Job Title column and start with Client */}
      <VuiBox display={{ xs: "none", sm: "flex" }} alignItems="center" gap={1.5} mt={1}>
        {cell("Client",       col.client)}
        {cell("Period",       { ...col.period })}
        {cell("Due",          col.due)}
        {cell("Assigned",     { ...col.manager })}
        {cell("Tasks",        { ...col.progress })}
      </VuiBox>
    </VuiBox>
  );
}

// â”€â”€â”€ Job row â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function JobRow({ job, tenantId, users, onStatusChange, onAssignChange, onNavigate, onViewTasks, onJobClick }) {
  return (
    <VuiBox
      data-job-id={job.id}
      display="flex" alignItems="center" gap={1.5} px={2} py={1.2}
      onClick={() => onJobClick(job)}
      sx={{
        ...dueDateRowStyle(job.dueDate),
        borderBottom: "1px solid #e4eef8",
        cursor: "pointer",
        "&:hover": { background: "rgba(0,117,255,0.06)" },
        transition: "background 0.15s",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
      }}
    >
      {/* Mobile compact layout (xs) */}
      <VuiBox display={{ xs: "flex", sm: "none" }} sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 0.6 }}>
        <VuiBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          {/* Left: status pill + title */}
          <VuiBox display="flex" alignItems="center" gap={0.75} sx={{ flex: 1, minWidth: 0 }}>
            {(() => {
              const cfg = STATUS_CFG[derivedStatus(job)] || STATUS_CFG.pending;
              return (
                <span style={{
                  display: "inline-block",
                  padding: "1px 8px", borderRadius: "20px",
                  background: `${cfg.color}22`, border: `1px solid ${cfg.color}55`,
                  color: cfg.color, fontSize: FS.xs, fontWeight: FW.bold, letterSpacing: "0.04em",
                  boxShadow: cfg.glow,
                }}>{cfg.label}</span>
              );
            })()}

            {/* Title removed per request: not rendering job.title here */}
          </VuiBox>

          {/* Right: Due chip */}
          <VuiBox data-field="due" sx={{ ml: 1, flexShrink: 0 }}>
            <DueChip dueDate={job.dueDate} />
          </VuiBox>
        </VuiBox>

        <VuiBox display="flex" alignItems="center" justifyContent="space-between" gap={1} sx={{ width: "100%" }}>
          <VuiBox display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
            <Tooltip title={job.clientName || job.clientId} enterTouchDelay={0}>
              <VuiTypography data-field="client" sx={{ color: TC.primary, fontSize: 13, fontWeight: FW.normal, fontFamily: 'inherit', overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                {job.clientName || job.clientId}
              </VuiTypography>
            </Tooltip>
            <Tooltip title="Open client profile">
              <IconButton size="small"
                onClick={(e) => { e.stopPropagation(); onNavigate(job.clientId); }}
                sx={{ p: 0.2, color: TC.secondary, "&:hover": { color: TC.linkHover } }}>
                <OpenInNewIcon sx={{ fontSize: "0.85rem" }} />
              </IconButton>
            </Tooltip>
          </VuiBox>

          <VuiBox display="flex" alignItems="center" gap={0.75}>
            <VuiBox display={{ xs: "flex", sm: "none" }} alignItems="center" gap={0.75}>
              <VuiTypography data-field="assigned" sx={{ color: TC.tertiary, fontSize: 12 }}>{job.assignedName || "Unassigned"}</VuiTypography>
              <VuiTypography data-field="tasks" sx={{ color: TC.primary, fontSize: 12 }}>{(job.completedCount || 0)}/{(job.taskCount || 0)}</VuiTypography>
            </VuiBox>

            <Tooltip title="View linked tasks">
              <IconButton
                size="small"
                aria-label={`View tasks for ${job.clientName || job.clientId}`}
                onClick={(e) => { e.stopPropagation(); onViewTasks(job.clientId); }}
                sx={{ p: 0.4, color: TC.secondary, "&:hover": { color: "#4caf50" }, flexShrink: 0 }}>
                <TaskAltIcon aria-hidden="true" sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Tooltip>
          </VuiBox>
        </VuiBox>
      </VuiBox>

      {/* Desktop/full-width row (sm+) - align to header columns */}
      <VuiBox display={{ xs: "none", sm: "flex" }} alignItems="center" gap={1} sx={{ width: "100%" }}>
        <VuiBox sx={{ ...col.client, minWidth: 0, display: "flex", alignItems: "center", gap: 0.75 }}>
          {(() => {
            const cfg = STATUS_CFG[derivedStatus(job)] || STATUS_CFG.pending;
            return (
              <span style={{
                display: "inline-block",
                padding: "1px 8px", borderRadius: "20px",
                background: `${cfg.color}22`, border: `1px solid ${cfg.color}55`,
                color: cfg.color, fontSize: FS.xs, fontWeight: FW.bold, letterSpacing: "0.04em",
                boxShadow: cfg.glow,
              }}>{cfg.label}</span>
            );
          })()}

          <Tooltip title={job.clientName || job.clientId} enterTouchDelay={0}>
            <VuiTypography sx={{ color: TC.primary, fontSize: 13, fontWeight: FW.normal, fontFamily: 'inherit', overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{job.clientName || job.clientId}</VuiTypography>
          </Tooltip>
          <Tooltip title="Open client profile">
            <IconButton size="small"
              onClick={(e) => { e.stopPropagation(); onNavigate(job.clientId); }}
              sx={{ p: 0.2, color: TC.secondary, "&:hover": { color: TC.linkHover } }}>
              <OpenInNewIcon sx={{ fontSize: "0.85rem" }} />
            </IconButton>
          </Tooltip>
        </VuiBox>

        <VuiBox sx={{ ...col.period }}>{job.period || "-"}</VuiBox>

        <VuiBox sx={{ ...col.due, display: "flex", alignItems: "center" }} data-field="due"><DueChip dueDate={job.dueDate} /></VuiBox>

        <VuiBox sx={{ ...col.manager, display: "flex", alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
          <AssignSelect job={job} tenantId={tenantId} users={users} onChange={onAssignChange} />
        </VuiBox>

        <VuiBox sx={{ ...col.progress, display: "flex", alignItems: "center", gap: 1 }}>
          <ProgressBar completed={job.completedCount || 0} total={job.taskCount || 0} />
          <Tooltip title="View linked tasks">
            <IconButton
              size="small"
              aria-label={`View tasks for ${job.clientName || job.clientId}`}
              onClick={(e) => { e.stopPropagation(); onViewTasks(job.clientId); }}
              sx={{ p: 0.4, color: TC.secondary, "&:hover": { color: "#4caf50" }, flexShrink: 0 }}>
              <TaskAltIcon aria-hidden="true" sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
        </VuiBox>
      </VuiBox>
    </VuiBox>
  );
}

// â”€â”€â”€ Filing task status cards â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const TASK_STAGE_CFG = [
  { key: "overdue",  label: "Overdue",   icon: "(!)", color: "#f44336", bg: "rgba(244,67,54,0.10)",  border: "rgba(244,67,54,0.30)"  },
  { key: "dueSoon",  label: "Due Soon",  icon: "(!)", color: "#ffb74d", bg: "rgba(255,152,0,0.10)",  border: "rgba(255,152,0,0.30)"  },
  { key: "upcoming", label: "Upcoming",  icon: "...", color: TC.link, bg: "rgba(144,202,249,0.08)", border: "rgba(144,202,249,0.20)" },
  { key: "done",     label: "Done",      icon: "v",   color: "#81c784", bg: "rgba(76,175,80,0.08)",  border: "rgba(76,175,80,0.20)"  },
];

function bucketTasks(tasks) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const daysUntil = (d) => d ? Math.ceil((new Date(d) - today) / 86400000) : null;
  const counts = { overdue: 0, dueSoon: 0, upcoming: 0, done: 0 };
  for (const t of tasks) {
    if (t.status === "completed" || t.status === "cancelled") { counts.done++; continue; }
    const d = daysUntil(t.dueDate);
    if (d === null)    { counts.upcoming++; continue; }
    if (d < 0)         counts.overdue++;
    else if (d <= 60)  counts.dueSoon++;
    else               counts.upcoming++;
  }
  return counts;
}

function FilingTaskCards({ tasks, onNavigate }) {
  const counts = bucketTasks(tasks);
  return (
    <Grid container spacing={2} mb={3}>
      {TASK_STAGE_CFG.map(({ key, label, icon, color, bg, border }) => (
        <Grid item xs={6} sm={3} key={key}>
          <Card
            onClick={() => onNavigate(key)}
            sx={{
              background: bg, border: `1px solid ${border}`, borderRadius: "14px",
              p: 2, cursor: "pointer", transition: "filter 0.15s",
              "&:hover": { filter: "brightness(1.15)" },
            }}
          >
            <VuiTypography sx={{ fontSize: "1.3rem", mb: 0.5 }}>{icon}</VuiTypography>
            <VuiTypography sx={{ color, fontSize: "1.6rem", fontWeight: 800, lineHeight: 1 }}>
              {counts[key]}
            </VuiTypography>
            <VuiTypography sx={{ color: TC.secondary, fontSize: "0.72rem", mt: 0.3, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              {label}
            </VuiTypography>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

// â”€â”€â”€ Grouped view (by service or by company) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function GroupedView({ jobs, tenantId, users, onJobClick, onNavigate, onStatusChange, onAssignChange, onViewTasks, groupBy }) {
  const [collapsed, setCollapsed] = useState({});

  const getKey   = (job) => groupBy === "company" ? (job.clientId  || "unknown") : (job._isTask ? `task_${job._taskType || job.type || "other"}` : (job.serviceId || "other"));
  const getLabel = (job) => groupBy === "company" ? (job.clientName || job.clientId || "Unknown") : (job._isTask ? (TASK_TYPE_LABELS[job._taskType || job.type] || job.title || "Tasks") : getServiceLabel(job.serviceId));

  const groups = jobs.reduce((acc, job) => {
    const key = getKey(job);
    if (!acc[key]) acc[key] = { label: getLabel(job), jobs: [] };
    acc[key].jobs.push(job);
    return acc;
  }, {});

  Object.values(groups).forEach(g =>
    g.jobs.sort((a, b) => {
      const da = a.dueDate ? new Date(a.dueDate) : new Date("9999");
      const db = b.dueDate ? new Date(b.dueDate) : new Date("9999");
      return da - db;
    })
  );

  const sortedGroups = Object.entries(groups).sort(([, a], [, b]) => {
    const ea = a.jobs[0]?.dueDate ? new Date(a.jobs[0].dueDate) : new Date("9999");
    const eb = b.jobs[0]?.dueDate ? new Date(b.jobs[0].dueDate) : new Date("9999");
    return ea - eb;
  });

  // Reset collapsed when groupBy changes
  useEffect(() => { setCollapsed({}); }, [groupBy]);

  return (
    <VuiBox>
      {sortedGroups.map(([key, group]) => {
        const isCollapsed = collapsed[key];
        const overdueCount = group.jobs.filter(j => overdueDays(j.dueDate) < 0 && j.status !== "filed" && j.status !== "cancelled").length;

        return (
          <VuiBox key={key} mb={2}>
            {/* Group header */}
            <VuiBox
              display="flex" alignItems="center" gap={1.5} px={2} py={1.2}
              onClick={() => setCollapsed(c => ({ ...c, [key]: !c[key] }))}
              sx={{
                background: "linear-gradient(135deg, #f4f8ff 0%, #e8f0fb 100%)",
                border: "1px solid #d8e6f7",
                borderRadius: isCollapsed ? "10px" : "10px 10px 0 0",
                cursor: "pointer",
                userSelect: "none",
                "&:hover": { background: "linear-gradient(135deg, #e8f0fb 0%, #dbeafe 100%)" },
              }}
            >
              <span style={{ fontSize: 14, color: TC.tertiary, transition: "transform 0.2s", display: "inline-block", transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)" }}>▼</span>

              <VuiTypography sx={{ fontSize: FS.md, fontWeight: FW.bold, color: TC.primary, flex: 1 }}>
                {group.label}
              </VuiTypography>

              <Chip
                label={`${group.jobs.length} job${group.jobs.length !== 1 ? "s" : ""}`}
                size="small"
                sx={{ bgcolor: "#0057d822", color: TC.link, fontSize: "0.68rem", fontWeight: FW.bold, border: "1px solid #0057d844" }}
              />
              {overdueCount > 0 && (
                <Chip
                  label={`${overdueCount} overdue`}
                  size="small"
                  sx={{ bgcolor: "rgba(244,67,54,0.15)", color: "#f44336", fontSize: "0.68rem", fontWeight: FW.bold }}
                />
              )}
              {group.jobs[0]?.dueDate && (
                <VuiTypography sx={{ fontSize: FS.xs, color: TC.tertiary, whiteSpace: "nowrap" }}>
                  Next due: {group.jobs[0].dueDate}
                </VuiTypography>
              )}
            </VuiBox>

            {/* Job rows */}
            {!isCollapsed && (
              <VuiBox sx={{ border: "1px solid #d8e6f7", borderTop: "none", borderRadius: "0 0 10px 10px", overflow: "hidden" }}>
                <TableHeader />
                {group.jobs.map(job => (
                  <JobRow
                    key={job.id}
                    job={job}
                    tenantId={tenantId}
                    users={users}
                    onStatusChange={onStatusChange}
                    onAssignChange={onAssignChange}
                    onNavigate={onNavigate}
                    onViewTasks={onViewTasks}
                    onJobClick={onJobClick}
                  />
                ))}
              </VuiBox>
            )}
          </VuiBox>
        );
      })}
    </VuiBox>
  );
}

// â”€â”€â”€ Summary chips â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function SummaryBar({ jobs }) {
  const counts = jobs.reduce((acc, j) => {
    acc[j.status] = (acc[j.status] || 0) + 1;
    return acc;
  }, {});
  const overdue = jobs.filter((j) => overdueDays(j.dueDate) < 0 && j.status !== "filed" && j.status !== "cancelled").length;

  return (
    <VuiBox display="flex" flexWrap="wrap" gap={1} mb={2}>
      <Chip label={`${jobs.length} total`} size="small" sx={{ bgcolor: "#7a94b0", color: TC.primary, fontSize: "0.7rem" }} />
      {Object.entries(STATUS_CFG).map(([k, v]) => counts[k]
        ? <Chip key={k} label={`${counts[k]} ${v.label}`} size="small" sx={{ bgcolor: `${v.color}22`, color: v.color, fontSize: "0.7rem", border: `1px solid ${v.color}44` }} />
        : null
      )}
      {overdue > 0 && <Chip label={`(!) ${overdue} overdue`} size="small" sx={{ bgcolor: "rgba(244,67,54,0.18)", color: "#f44336", fontSize: "0.7rem", fontWeight: 700 }} />}
    </VuiBox>
  );
}

// â”€â”€â”€ Main page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export function JobsBoardContent() {
  const { tenantId } = useAuthz();
  const history      = useHistory();

  const [jobs,    setJobs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [users,   setUsers]   = useState([]);
  const [tasks,   setTasks]   = useState([]);

  // Filters
  const [activeTab,      setActiveTab]      = useState("all");
  const [filterStatus,   setFilterStatus]   = useState("");
  const [filterManager,  setFilterManager]  = useState("");
  const [filterDuration, setFilterDuration] = useState("3months");
  const [search,         setSearch]         = useState("");
  const [page,           setPage]           = useState(0);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [jobsRes, usersRes, tasksRes, clientsRes] = await Promise.all([
        listClientJobs(tenantId, null),
        listTenantUsers({ tenantId, limit: 200 }),
        listTasks(tenantId, {}),
        listClientProfiles({ tenantId, status: "active" }),
      ]);

      const activeClientIds = new Set(
        (clientsRes.clientProfiles || []).map((c) => c.id || c._id || c.profileId)
      );

      const rawTasks = tasksRes.tasks || [];
      const taskRows = rawTasks.map(t => ({
        ...t,
        _isTask:        true,
        _taskType:      t.type,
        serviceId:      null,
        completedCount: 0,
        taskCount:      0,
      }));

      const activeJobs = (jobsRes.jobs || []).filter((j) =>
        !j.clientId || activeClientIds.has(j.clientId)
      );

      const all = [...activeJobs, ...taskRows].sort((a, b) => {
        const da = a.dueDate ? new Date(a.dueDate) : new Date("9999");
        const db = b.dueDate ? new Date(b.dueDate) : new Date("9999");
        return da - db;
      });

      setJobs(all);
      // Debug: expose a small sample to window and print it so we can validate column → field mapping in the UI
      // Access `window.__jobsSample` from the browser console to inspect objects.
      // Remove or disable in production if noisy.
      // eslint-disable-next-line no-console
      console.debug("[JobsBoard] loaded jobs sample:", all.slice(0, 5));
      // Expose the sample globally for quick interactive debugging in DevTools
      try { window.__jobsSample = all; } catch (e) { /* ignore in non-browser env */ }
      setUsers((usersRes.users || []).filter((u) => u.active !== false));
      setTasks(rawTasks);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [load]);

  function handleStatusChange(jobId, newStatus) {
    setJobs((prev) => prev.map((j) => j.id === jobId ? { ...j, status: newStatus } : j));
  }
  function handleAssignChange(jobId, assignedTo, assignedName) {
    setJobs((prev) => prev.map((j) => j.id === jobId ? { ...j, assignedTo, assignedName } : j));
  }
  function handleNavigate(clientId) {
    history.push(`/client-profiles/${clientId}`, { tab: 6 });
  }
  function handleJobClick(job) {
    const jobTab = job._isTask
      ? (TASK_TYPE_TAB_INDEX[job.type] ?? 3)
      : (SERVICE_TAB_INDEX[job.serviceId] ?? 0);
    history.push(`/client-profiles/${job.clientId}`, { tab: 6, jobTab });
  }
  function handleViewTasks(clientId) {
    history.push(`/tasks?clientId=${encodeURIComponent(clientId)}`);
  }

  // -- Static service tabs (always visible regardless of whether jobs exist)
  const SERVICE_TABS = [
    { key: "payroll",             label: "PAYE"                        },
    { key: "vat",                 label: "VAT"                         },
    { key: "accounts",            label: "Accounts Prep"               },
    { key: "corporation_tax",     label: "CT600"                       },
    { key: "self_assessment",     label: "Self Assessment"             },
    { key: "ad01_address_change", label: "Address Change (AD01)"       },
    { key: "mtd_quarterly",       label: "MTD Quarterly"               },
  ];

  const hasTasks = jobs.some(j => j._isTask);
  const tabs = [
    { key: "all", label: "All Jobs" },
    ...SERVICE_TABS,
    ...(hasTasks ? [{ key: "_tasks", label: "Filing Tasks" }] : []),
  ];

  // â”€â”€ Apply filters â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const filtered = (() => {
    let f = applyDurationFilter(jobs, filterDuration);

    // Tab filter
    if (activeTab === "_tasks") {
      f = f.filter(j => j._isTask);
    } else if (activeTab !== "all") {
      f = f.filter(j => !j._isTask && j.serviceId === activeTab);
    }

    if (filterStatus) {
      f = f.filter(j => derivedStatus(j) === filterStatus || j.status === filterStatus);
    }
    if (filterManager === "__unassigned__") {
      f = f.filter(j => !j.assignedTo && !j.assignedName);
    } else if (filterManager) {
      f = f.filter(j =>
        (j.assignedName || "").toLowerCase().includes(filterManager.toLowerCase()) ||
        (j.assignedTo   || "").toLowerCase().includes(filterManager.toLowerCase())
      );
    }
    if (search) {
      const q = search.toLowerCase();
      f = f.filter(j =>
        (j.title       || "").toLowerCase().includes(q) ||
        (j.clientName  || "").toLowerCase().includes(q) ||
        (j.serviceId   || "").toLowerCase().includes(q)
      );
    }
    return f;
  })();

  const managers = [...new Set(jobs.map(j => j.assignedName).filter(Boolean))];
  const hasFilters = filterStatus || filterManager || filterDuration !== "3months" || search;

  const pageJobs = filtered.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

  return (
    <VuiBox py={3} px={{ xs: 1, md: 2 }}>

        {/* â”€â”€ Page header â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={2.5} flexWrap="wrap" gap={2}>
          <VuiBox>
            <VuiTypography variant="h5" fontWeight="bold" sx={{ color: TC.primary }}>Jobs Board</VuiTypography>
            <VuiTypography sx={{ color: TC.tertiary, fontSize: FS.sm }}>
              All active client jobs ordered by due date
            </VuiTypography>
          </VuiBox>
          <VuiBox display="flex" alignItems="center" gap={1}>
            {!loading && (
              <Chip
                label={`${filtered.length} job${filtered.length !== 1 ? "s" : ""}`}
                size="small"
                sx={{ bgcolor: "rgba(0,117,255,0.1)", color: TC.link, fontSize: FS.xs,
                  border: "1px solid rgba(0,117,255,0.25)", height: 24, fontWeight: FW.semibold }}
              />
            )}
            <Tooltip title="Refresh">
              <IconButton aria-label="Refresh jobs board" onClick={load} size="small"
                sx={{ color: TC.secondary, border: "1px solid #d8e6f7", borderRadius: "8px",
                  "&:hover": { color: TC.primary, background: "#f4f8ff" } }}>
                <RefreshIcon aria-hidden="true" sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Tooltip>
          </VuiBox>
        </VuiBox>

        {/* â”€â”€ Main card â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <Card sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "16px" }}>

          {/* â”€â”€ Tabs â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <VuiBox sx={{ borderBottom: "1px solid #e4eef8", px: 2, pt: 1.5 }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => { setActiveTab(v); setPage(0); }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                minHeight: 36,
                "& .MuiTabs-indicator": { backgroundColor: TC.link, height: 2 },
                "& .MuiTab-root": {
                  color: TC.secondary, fontSize: FS.sm, minHeight: 36,
                  textTransform: "none", fontWeight: FW.medium, py: 0.5, px: 1.5,
                },
                "& .Mui-selected": { color: `${TC.link} !important`, fontWeight: `${FW.bold} !important` },
                "& .MuiTabScrollButton-root": { color: TC.secondary },
              }}
            >
              {tabs.map((t) => {
                const count = (() => {
                  let f = applyDurationFilter(jobs, filterDuration);
                  if (t.key === "_tasks") return f.filter(j => j._isTask).length;
                  if (t.key === "all")    return f.length;
                  return f.filter(j => !j._isTask && j.serviceId === t.key).length;
                })();
                return (
                  <Tab
                    key={t.key}
                    value={t.key}
                    label={
                      <VuiBox display="flex" alignItems="center" gap={0.6}>
                        <span>{t.label}</span>
                        {count > 0 && (
                          <span style={{
                            background: activeTab === t.key ? TC.link : "#e4eef8",
                            color:      activeTab === t.key ? "#fff"   : TC.secondary,
                            fontSize: "0.62rem", fontWeight: 700,
                            padding: "1px 6px", borderRadius: "10px", lineHeight: 1.6,
                          }}>
                            {count}
                          </span>
                        )}
                      </VuiBox>
                    }
                  />
                );
              })}
            </Tabs>
          </VuiBox>

          {/* â”€â”€ Filter bar â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          {/* Filter bar: explicit two rows so search is always on its own line on small screens */}
          <VuiBox px={1.5} py={1.5} sx={{ borderBottom: "1px solid #e4eef8" }}>
            {/* Row 1: Search (always full width on xs; occupies left half on sm+) */}
            <VuiBox display="flex" alignItems="center" gap={0.75}>
              <TextField
                size="small"
                placeholder="Search..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: TC.secondary, fontSize: "0.85rem" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: "100%", sm: "50%" },
                  "& .MuiOutlinedInput-root": {
                    height: 34, fontSize: 12, color: TC.primary,
                    "& fieldset": { borderColor: "#d8e6f7" },
                    "&:hover fieldset": { borderColor: "#b0cce8" },
                    "&.Mui-focused fieldset": { borderColor: TC.link },
                  },
                }}
              />
            </VuiBox>

            {/* Row 2: Filters (on xs each filter stretches full width; on sm+ display inline) */}
            <VuiBox display="flex" alignItems="center" gap={0.75} mt={1}
              sx={{ flexWrap: "wrap", alignItems: { xs: "stretch", sm: "center" } }}>

              <Select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
                size="small" displayEmpty
                sx={{ ...SELECT_SX, height: 34, fontSize: 12, flex: { xs: "1 1 100%", sm: "1 1 0" }, minWidth: 0,
                  "& .MuiSelect-select": { py: "6px", pr: "28px !important", pl: "14px",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }}>
                <MenuItem value="" sx={{ fontSize: 11 }}><em style={{ color: TC.secondary, fontStyle: "normal" }}>Status</em></MenuItem>
                {Object.entries(STATUS_CFG).map(([k, v]) => (
                  <MenuItem key={k} value={k} sx={{ fontSize: 11 }}>{v.label}</MenuItem>
                ))}
              </Select>

              <Select value={filterManager} onChange={(e) => { setFilterManager(e.target.value); setPage(0); }}
                size="small" displayEmpty
                sx={{ ...SELECT_SX, height: 34, fontSize: 12, flex: { xs: "1 1 100%", sm: "1 1 0" }, minWidth: 0,
                  "& .MuiSelect-select": { py: "6px", pr: "28px !important", pl: "14px",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }}>
                <MenuItem value="" sx={{ fontSize: 11 }}><em style={{ color: TC.secondary, fontStyle: "normal" }}>Assignee</em></MenuItem>
                {managers.map((m) => (
                  <MenuItem key={m} value={m} sx={{ fontSize: 11 }}>{m}</MenuItem>
                ))}
                <MenuItem value="__unassigned__" sx={{ fontSize: 11 }}>Unassigned</MenuItem>
              </Select>

              <Select value={filterDuration} onChange={(e) => { setFilterDuration(e.target.value); setPage(0); }}
                size="small"
                sx={{ ...SELECT_SX, height: 34, fontSize: 12, flex: { xs: "1 1 100%", sm: "1 1 0" }, minWidth: 0,
                  "& .MuiSelect-select": { py: "6px", pr: "28px !important", pl: "14px",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }}>
                {DURATION_OPTIONS.map(({ value, label }) => (
                  <MenuItem key={value} value={value} sx={{ fontSize: 11 }}>{label}</MenuItem>
                ))}
              </Select>

              {hasFilters && (
                <Chip label="Clear" size="small" onClick={() => {
                  setSearch(""); setFilterStatus(""); setFilterManager(""); setFilterDuration("3months"); setPage(0);
                }}
                sx={{ cursor: "pointer", bgcolor: "#e4eef8", color: TC.secondary, height: 22,
                  fontSize: 11, flexShrink: 0, "&:hover": { bgcolor: "#d0e2f4" } }} />
              )}

            </VuiBox>
          </VuiBox>

          {/* â”€â”€ Job list â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          {loading ? (
            <VuiBox display="flex" justifyContent="center" alignItems="center" py={6}>
              <CircularProgress size={32} />
            </VuiBox>
          ) : filtered.length === 0 ? (
            <VuiBox display="flex" flexDirection="column" alignItems="center" py={6} gap={1}>
              <VuiTypography sx={{ color: TC.primary, fontSize: 15 }}>No jobs found</VuiTypography>
              <VuiTypography sx={{ color: TC.secondary, fontSize: 13 }}>
                {hasFilters ? "Try clearing filters." : "Assign a service to a client to auto-generate jobs."}
              </VuiTypography>
            </VuiBox>
          ) : (
            <>
              <TableHeader />
              {pageJobs.map((job) => (
                <JobRow
                  key={job.id}
                  job={job}
                  tenantId={tenantId}
                  users={users}
                  onStatusChange={handleStatusChange}
                  onAssignChange={handleAssignChange}
                  onNavigate={handleNavigate}
                  onViewTasks={handleViewTasks}
                  onJobClick={handleJobClick}
                />
              ))}
              {filtered.length > ROWS_PER_PAGE && (
                <TablePagination
                  component="div"
                  count={filtered.length}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={ROWS_PER_PAGE}
                  rowsPerPageOptions={[ROWS_PER_PAGE]}
                  sx={{
                    color: TC.secondary, fontSize: 13,
                    "& .MuiTablePagination-actions button": { color: TC.secondary },
                    "& .MuiTablePagination-actions button:hover": { color: TC.primary },
                    "& .MuiTablePagination-actions button.Mui-disabled": { color: "#b0c8e0" },
                  }}
                />
              )}
            </>
          )}
        </Card>

      </VuiBox>
  );
}

export default function JobsBoard() {
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <JobsBoardContent />
      <Footer />
    </DashboardLayout>
  );
}

